import { z } from "zod";

/**
 * The smart inbox — PRD FR-6.4. The GET /inbox reads (views + ~12 filters) are
 * parsed from the query string in the route; these schemas cover the three
 * mutating endpoints, validated at the trust boundary.
 */

/** The five composer actions (helpful/not_helpful is the separate feedback path). */
export const InboxActionKind = z.enum(["reply", "book", "save", "not_interested", "block"]);
export type InboxActionKind = z.infer<typeof InboxActionKind>;

/**
 * FR-8.2 reason chips for FEEDBACK ON THE RANKING — "what was off about it".
 *
 * These are notes about how a knock was scored, and `shouldnt_surface` is the
 * one of them that moves anything (`inbox/actions.ts`). They are no longer the
 * dismissal's vocabulary: a dismissal is not a grade, and the two lists said
 * different things under one name for long enough.
 */
export const InboxReasonChip = z.enum([
  "shouldnt_surface",
  "ranked_too_low",
  "summary_wrong",
  "wrong_category",
  "good_sender_bad_timing",
  "other",
]);
export type InboxReasonChip = z.infer<typeof InboxReasonChip>;

/**
 * FR-6.4 dismissal reasons — "why is it not for you?" (owner item 10).
 *
 * A DIFFERENT QUESTION FROM THE ONE ABOVE, which is why it is a different enum.
 * The ranking chips grade Nob's work; these say what the owner made of the
 * PERSON — not interested, no evidence, a scam, bad timing. `shouldnt_passed`
 * is the only one that is also about the door, and the client offers it only on
 * a knock that actually passed, because "shouldn't have passed" is nonsense
 * about one that did not.
 */
export const InboxDismissChip = z.enum([
  "shouldnt_passed",
  "not_interested",
  "not_enough_evidence",
  "spam_scam",
  "good_sender_bad_timing",
  "other",
]);
export type InboxDismissChip = z.infer<typeof InboxDismissChip>;

/** What the free-text behind "Other" may be (FR-6.4). One line, not an essay. */
export const DISMISS_NOTE_MAX = 280;

export const InboxActionRequest = z
  .object({
    knock_id: z.uuid(),
    action: InboxActionKind,
    /** Not-interested prompts for one; free-form chips also accepted (max 64). */
    reason_chip: z.string().trim().max(64).optional(),
    /** Reply body for `reply`; the recipient's own message (max 5,000 chars). */
    note: z.string().trim().max(5000).optional(),
  })
  .superRefine((input, ctx) => {
    /**
     * THE DISMISSAL'S CHIPS ARE CLOSED, and only the dismissal's.
     *
     * `reason_chip` stays a free string on the object so the other verbs keep
     * carrying whatever they carry, but "not for me" now has a vocabulary the
     * owner picked, and a value outside it is a client that has drifted from
     * it rather than a reason worth recording.
     */
    if (input.action === "not_interested" && input.reason_chip !== undefined) {
      if (!InboxDismissChip.safeParse(input.reason_chip).success) {
        ctx.addIssue({
          code: "custom",
          path: ["reason_chip"],
          message: `not_interested takes one of: ${InboxDismissChip.options.join(", ")}`,
        });
      }
    }
    /**
     * A REPLY IS A MESSAGE; EVERY OTHER NOTE IS A REMARK. Five thousand
     * characters is the size of something written to a person, and nothing but
     * `reply` writes to a person — the dismissal's "Other" is one line about
     * why, and it is held to one line's worth.
     */
    if (input.action !== "reply" && (input.note?.length ?? 0) > DISMISS_NOTE_MAX) {
      ctx.addIssue({
        code: "custom",
        path: ["note"],
        message: `note must be ${DISMISS_NOTE_MAX} characters or fewer`,
      });
    }
  });
export type InboxActionRequest = z.infer<typeof InboxActionRequest>;

export const InboxUndoRequest = z.object({
  action_id: z.uuid(),
});
export type InboxUndoRequest = z.infer<typeof InboxUndoRequest>;

/**
 * Lifting one block from the Blocked-users list. The id names a blocklist ROW;
 * the sender behind it is resolved server-side, so nothing here is an identifier
 * for a person.
 */
export const InboxUnblockRequest = z.object({
  block_id: z.uuid(),
});
export type InboxUnblockRequest = z.infer<typeof InboxUnblockRequest>;

export const InboxFeedbackRequest = z.object({
  knock_id: z.uuid(),
  helpful: z.boolean(),
  reason_chip: InboxReasonChip.optional(),
});
export type InboxFeedbackRequest = z.infer<typeof InboxFeedbackRequest>;
